import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown, FileText } from "lucide-react";
import { useState, useEffect } from "react";
import { PHONE_NUMBER, PHONE_HREF } from "./EmergencyBanner";
import logo from "@/assets/logo.png";

const mainNav = [
  {
    label: "Services",
    href: "/services",
    children: [
      { label: "Well Pump Repair", href: "/services/well-pump-repair", desc: "Fast diagnosis & repair" },
      { label: "Well Pump Installation", href: "/services/well-pump-installation", desc: "New pump systems" },
      { label: "Grinder Pump Repair", href: "/services/grinder-pump-repair", desc: "Alarm & failure service" },
      { label: "Sewage Pump Systems", href: "/services/sewage-pump-systems", desc: "Wastewater solutions" },
      { label: "Water Filtration", href: "/services/water-filtration", desc: "Clean, safe water" },
      { label: "Emergency Service", href: "/services/emergency-pump-service", desc: "Same-day response" },
    ],
  },
  { label: "Service Area", href: "/service-area" },
  { label: "Reviews", href: "/reviews" },
  { label: "Resources", href: "/resources" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
];

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        scrolled
          ? "bg-background/95 glass shadow-elevated"
          : "bg-background shadow-sm"
      }`}
    >
      <div className="container flex items-center justify-between h-16 lg:h-[72px]">
        {/* Logo */}
        <Link to="/" className="flex items-center shrink-0">
          <img src={logo} alt="Semper Fi Well & Pump Services" className="h-12 lg:h-14 w-auto" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1 ml-8">
          {mainNav.map((link) => {
            const isActive = location.pathname === link.href || (link.children && location.pathname.startsWith("/services"));
            return (
              <div key={link.href} className="relative group">
                <Link
                  to={link.href}
                  className={`relative px-3.5 py-2 text-[13px] font-semibold rounded-lg inline-flex items-center gap-1 transition-colors ${
                    isActive ? "text-accent" : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  {link.label}
                  {link.children && <ChevronDown className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-transform group-hover:rotate-180" />}
                </Link>

                {link.children && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 hidden group-hover:block">
                    <div className="bg-background/95 glass rounded-xl shadow-elevated border border-border p-3 w-[440px] grid grid-cols-2 gap-1">
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="flex flex-col px-4 py-3 rounded-lg hover:bg-secondary/80 group/item transition-colors"
                        >
                          <span className="text-sm font-semibold text-foreground group-hover/item:text-accent transition-colors">{child.label}</span>
                          <span className="text-xs text-muted-foreground mt-0.5">{child.desc}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-2.5 ml-4 shrink-0">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-5 py-2.5 rounded-lg text-sm hover:bg-primary/90 transition-colors shadow-sm"
          >
            <FileText className="w-4 h-4" />
            Free Quote
          </Link>
          <a
            href={PHONE_HREF}
            className="inline-flex items-center gap-2 gradient-accent text-accent-foreground font-semibold px-5 py-2.5 rounded-lg text-sm hover:opacity-90 transition-opacity shadow-glow"
          >
            <Phone className="w-4 h-4" />
            Call Now
          </a>
        </div>

        {/* Mobile */}
        <div className="flex items-center gap-2 lg:hidden">
          <a
            href={PHONE_HREF}
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg gradient-accent text-accent-foreground shadow-glow"
            aria-label="Call now"
          >
            <Phone className="w-5 h-5" />
          </a>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-foreground hover:bg-secondary transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-border bg-background max-h-[calc(100vh-4rem)] overflow-y-auto">
          <nav className="container py-4">
            {mainNav.map((link) => (
              <div key={link.href}>
                {link.children ? (
                  <>
                    <button
                      onClick={() => setServicesOpen(!servicesOpen)}
                      className="flex items-center justify-between w-full px-3 py-3 text-sm font-semibold text-foreground hover:text-accent transition-colors"
                    >
                      {link.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                    </button>
                    {servicesOpen && (
                      <div className="pl-4 pb-2 space-y-1">
                        <Link to={link.href} className="block px-3 py-2 text-sm text-muted-foreground hover:text-accent transition-colors">
                          All Services
                        </Link>
                        {link.children.map((child) => (
                          <Link key={child.href} to={child.href} className="block px-3 py-2 text-sm text-muted-foreground hover:text-accent transition-colors">
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.href}
                    className={`block px-3 py-3 text-sm font-semibold transition-colors ${
                      location.pathname === link.href ? "text-accent" : "text-foreground hover:text-accent"
                    }`}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
            <div className="mt-4 pt-4 border-t border-border space-y-2.5">
              <Link to="/contact" className="flex items-center justify-center w-full bg-primary text-primary-foreground font-semibold py-3 rounded-lg text-sm transition-colors">
                <FileText className="w-4 h-4 mr-2" />
                Free Quote
              </Link>
              <a href={PHONE_HREF} className="flex items-center justify-center w-full gradient-accent text-accent-foreground font-semibold py-3 rounded-lg text-sm">
                <Phone className="w-4 h-4 mr-2" />
                Call {PHONE_NUMBER}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
